import { useLayoutEffect, useRef } from 'react';
import { WizardStepsProps } from './types';
import { WizardFooter } from './WizardFooter';
import { WizardSteps } from './WizardSteps';

interface WizardProps {
  steps: WizardStepsProps[];
  currentStep: number;
}

const isVerticalScroller = (el: HTMLElement) => {
  const { overflowY } = getComputedStyle(el);
  return overflowY === 'auto' || overflowY === 'scroll';
};

/**
 * [MCV-1883] A step change swaps the children in place, so the surrounding scrollers keep the
 * previous step's scrollTop and the next step opens part-way down. Every wizard renders in a
 * portaled ModalDialog/FormDialog, which nests two: the dialog body, and the overlay around the
 * panel (it scrolls too on a short viewport, where the body's scroll chains into it). Reset each
 * scrolled one, in a layout effect so the new step never paints at the old offset. Skipped on the
 * first mount, which has nothing to reset.
 */
const useResetScrollOnStepChange = (currentStep: number) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const previousStep = useRef(currentStep);

  useLayoutEffect(() => {
    if (previousStep.current === currentStep) return;
    previousStep.current = currentStep;

    // Not just the first overflow wrapper: one with no scroll range may sit inside the one that
    // holds the old offset. The document is left alone; behind a dialog it is the page.
    for (let el = rootRef.current?.parentElement ?? null; el; el = el.parentElement) {
      if (isVerticalScroller(el) && el.scrollTop > 0) el.scrollTop = 0;
    }
  }, [currentStep]);

  return rootRef;
};

export const Wizard = (props: WizardProps) => {
  const { steps, currentStep } = props;
  const rootRef = useResetScrollOnStepChange(currentStep);
  return (
    <div ref={rootRef} className="max-w-full">
      <WizardSteps currentStep={currentStep} steps={steps} />
      <h1
        data-cy={steps[currentStep].cyTag}
        className="mb-4 text-left sub-heading-sb text-grey-100"
      >
        {steps[currentStep].header}
      </h1>
      <div className="flex flex-col gap-5">
        {steps[currentStep].children({ footer: <WizardFooter {...steps[currentStep].footer} /> })}
      </div>
    </div>
  );
};
